import * as React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Opens (or gets) a DM with another member. Member picker via the same
 * search endpoint used elsewhere.
 */
export default function NewDMDialog({ open, onOpenChange, companyId, currentUserId, onOpened }) {
  const [q, setQ] = React.useState("");
  const [members, setMembers] = React.useState([]);
  const [busyId, setBusyId] = React.useState(null);

  React.useEffect(() => {
    if (!open) { setQ(""); setMembers([]); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/search/members`, {
          params: { q, limit: 20 },
        });
        setMembers((data.members || []).filter((m) => m.id !== currentUserId));
      } catch {
        setMembers([]);
      }
    }, 100);
    return () => clearTimeout(h);
  }, [open, q, companyId, currentUserId]);

  async function pick(m) {
    if (busyId) return;
    setBusyId(m.id);
    try {
      const { data } = await api.post(`/companies/${companyId}/comms/dms`, { with_user_id: m.id });
      onOpened && onOpened(data);
      onOpenChange(false);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not open DM");
    } finally {
      setBusyId(null);
    }
  }

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/30 flex items-start justify-center pt-24 px-4"
      role="dialog"
      aria-modal="true"
      onClick={() => onOpenChange(false)}
      data-testid="comms-new-dm-dialog"
    >
      <div
        className="w-full max-w-md bg-card rounded-lg shadow-card border border-brand-rule overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-brand-rule">
          <div className="micro-label">Comms · direct message</div>
          <div className="mt-0.5 text-[15px] font-semibold text-brand-ink">Start a DM</div>
        </div>
        <div className="px-5 pt-3">
          <input
            className="field-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search members…"
            autoFocus
            data-testid="comms-new-dm-search"
          />
        </div>
        <div className="mt-2 max-h-72 overflow-y-auto">
          {members.length === 0 ? (
            <div className="px-5 py-6 text-[12px] text-brand-muted text-center">
              No members match "{q}".
            </div>
          ) : (
            members.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => pick(m)}
                disabled={!!busyId}
                className="w-full flex items-center gap-2 px-5 py-2 text-left text-[12.5px] hover:bg-brand-hush"
                data-testid={`comms-new-dm-option-${m.id}`}
              >
                <span className="h-6 w-6 rounded-full bg-brand-violet text-white text-[10px] font-bold flex items-center justify-center">
                  {(m.name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="font-medium text-brand-ink">{m.name}</span>
                <span className="text-brand-muted truncate">{m.email}</span>
                {busyId === m.id && <span className="ml-auto mono-num text-[10px] chip">opening…</span>}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
